import { DownOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import React from "react";
import SVG_ICON from "@/assets/icons/svg";
import HelperManager from "@/helper/HelperManager";
import useResizeObserver from "@/hooks/useResizeObserver";
import useMasterDataState from "@/stores/MasterDataState";
import type { IPhonePickerComponentProps } from "./_models";
import _PhonePickerAdmissionController from "./_PhonePickerAdmissionController";

const PhonePickerAdmissionComponent = React.forwardRef<
  HTMLInputElement,
  IPhonePickerComponentProps
>(
  (
    {
      error,
      required,
      value,
      countryCodeValue,
      disabled,
      title,
      onChange,
      containerStyle,
      tabIndex,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const controller = _PhonePickerAdmissionController(value, countryCodeValue);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = React.useState(false);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const positionClass = controller.isTopPosition ? "bottom-[40px]" : "top-[40px]";
    const [contentRef, contentSize] = useResizeObserver<HTMLParagraphElement>();
    const masterDataState = useMasterDataState();

    const focusInput = () => {
      if (!disabled && inputRef.current) {
        inputRef.current.focus();
      }
    };

    return (
      <div
        className={HelperManager.cn(
          clsx({
            "relative flex flex-col border border-gray-300 rounded-md": true,
            "focus-within:border-[#667FFF] focus-visible:outline-none": true,
            "border-[#667FFF]": isFocused,
            "hover:border-primary-color": !disabled,
            "!border-primary-error-color": !!error,
          }),
          containerStyle,
        )}
        onClick={focusInput}
      >
        <p
          className="absolute -z-500 left-[-9999px] items-center text-sm font-normal "
          ref={contentRef}
        >
          {controller?.contact?.dialCode}
        </p>
        {title && (
          <p className={"text-sm font-semibold text-[#00000080] "}>
            {title}{" "}
            {required && <span className="text-sm font-semibold text-primary-error-color ">*</span>}
          </p>
        )}

        <div
          className="relative flex h-[37px] flex-row items-center justify-center"
          ref={controller?.dropdownRef}
        >
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              controller?._handleToggleDropdown();
              focusInput();
            }}
            className={clsx({
              "ml-[4px] flex h-[100%] w-[50px] flex-row items-center justify-between border-r border-gray-200": true,
              "cursor-not-allowed opacity-50": disabled,
            })}
          >
            <img
              className="shadow-md"
              src={controller?.contact?.flagUrl || ""}
              alt={""}
              width={18}
              height={14}
            />
            <div
              className={clsx({
                "transition-all duration-300": true,
                "rotate-180": controller?.isOpen,
              })}
            >
              <DownOutlined
                style={{
                  marginLeft: "4px",
                  marginRight: "4px",
                  fontSize: "12px",
                }}
              />
            </div>
          </button>

          <div
            className="flex h-full items-center pl-[4px]"
            style={{ width: contentSize.width + 8 }}
          >
            <input
              className={clsx({
                "flex h-full min-w-[40px] items-center text-sm font-normal ": true,
                "opacity-50": disabled,
              })}
              readOnly
              value={controller?.contact?.dialCode}
              tabIndex={tabIndex}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  controller?._handleToggleDropdown();
                }
              }}
            />
          </div>
          <input
            ref={inputRef}
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => {
              const value = event.target.value;
              if (/^\d*$/.test(value)) {
                controller?.setPhoneInputValue(value);
                const syntheticEvent = {
                  target: {
                    value: {
                      formattedNumber: `${value ? `${controller?.contact?.dialCode} ${value}` : ""}`,
                      countryCode: controller?.contact?.code ?? "",
                      phoneNumber: value,
                    },
                  },
                };
                onChange?.(
                  syntheticEvent as unknown as {
                    formattedNumber: string;
                    countryCode: string;
                    phoneNumber: string;
                  },
                );
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "e") {
                event.preventDefault();
              }
            }}
            value={controller?.phoneInputValue}
            className={clsx({
              "flex h-full w-full flex-1 text-sm font-normal  focus:outline-none focus:ring-0": true,
              "disabledInput cursor-not-allowed opacity-50": disabled,
            })}
            placeholder={placeholder}
            type="text"
            maxLength={12}
            tabIndex={tabIndex && tabIndex > 1 ? tabIndex + 1 : -1}
            {...props}
          />
          {controller?.isOpen && !disabled && (
            <div className="absolute bottom-0 left-0 right-0 top-0 flex flex-1 bg-transparent">
              <div
                className={HelperManager.cn(
                  "absolute z-[9999] max-h-[160px] w-[100%] overflow-y-auto rounded-lg border bg-white shadow-lg scrollbar-hide",
                  positionClass,
                )}
              >
                <div
                  className={clsx({
                    "group flex h-10 w-[100%] flex-row items-center justify-between border-b": true,
                    "focus-within:border-primary-color": true,
                  })}
                >
                  <input
                    className="flex h-[100%] flex-1 pl-[16px] text-left text-sm font-normal "
                    value={controller.searchValue}
                    onChange={(e) => {
                      controller.setSearchValue(e.target.value);
                    }}
                    placeholder="Search..."
                  />

                  <button
                    type="button"
                    onClick={() => {
                      controller.setSearchValue("");
                    }}
                    className={clsx({
                      "mr-2 flex size-[24px] cursor-pointer items-center justify-center rounded-full": true,
                      "opacity-0": !HelperManager.isValid(controller.searchValue),
                    })}
                  >
                    <img src={SVG_ICON.XCircleIcon} alt="" />
                  </button>
                </div>

                {masterDataState.nationality
                  .filter((country) => {
                    if (
                      country.country
                        ?.toLowerCase()
                        ?.includes(controller.searchValue.toLowerCase()) ||
                      country.dialCode?.includes(controller.searchValue.toLowerCase())
                    ) {
                      return true;
                    }

                    return false;
                  })
                  .map((value) => (
                    <button
                      type="button"
                      onTouchStart={() => {
                        controller?.setContact(value);
                        controller?._handleToggleDropdown();
                        if (controller.phoneInputValue) {
                          onChange({
                            formattedNumber: `${value.dialCode} ${controller.phoneInputValue || ""}`,
                            countryCode: value.code,
                            phoneNumber: controller.phoneInputValue || "",
                          });
                        }
                      }}
                      onClick={() => {
                        controller?.setContact(value);
                        controller?._handleToggleDropdown();
                        if (controller.phoneInputValue) {
                          onChange({
                            formattedNumber: `${value.dialCode} ${controller.phoneInputValue || ""}`,
                            countryCode: value.code,
                            phoneNumber: controller.phoneInputValue || "",
                          });
                        }
                      }}
                      key={`${value.code}`}
                      className={clsx({
                        "flex min-h-[30px] w-[100%] cursor-pointer items-center pl-[16px] transition-all hover:bg-accent hover:text-black": true,
                      })}
                    >
                      <img
                        className="shadow-md"
                        src={value?.flagUrl || ""}
                        alt={""}
                        width={18}
                        height={14}
                      />
                      <p className="min-w-[60px] px-[8px] text-left text-sm font-normal ">{`${value.dialCode}`}</p>
                      <p className="text-left text-sm font-normal ">{`${value.country}`}</p>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default PhonePickerAdmissionComponent;
